class QuoteForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('form');
    this.fileInput = this.querySelector('[data-quote-file-input]');
    this.fileList = this.querySelector('[data-quote-file-list]');
    this.photoUrlsInput = this.querySelector('[data-quote-photo-urls]');
    this.statusEl = this.querySelector('[data-quote-status]');
    this.submitBtn = this.querySelector('[data-quote-submit]');
    this.files = [];
    this.isSubmitting = false;

    this.supabaseUrl = this.dataset.supabaseUrl;
    this.supabaseKey = this.dataset.supabaseKey;
    this.bucket = this.dataset.supabaseBucket || 'quote-uploads';
    this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 6;
    this.maxFileSizeMb = parseInt(this.dataset.maxFileSizeMb, 10) || 10;

    this.areaSelect = this.querySelector('[data-quote-area]');
    this.phoneInput = this.querySelector('[data-quote-phone]');
    this.phoneValue = this.querySelector('[data-quote-phone-value]');

    this.bindEvents();
    this.setupPhone();
    this.setupOther();
    this.setupVehicleCascade();
    this.preselectChargerFromUrl();
  }

  optEl(value, label) {
    const o = document.createElement('option');
    o.value = value;
    o.textContent = label;
    return o;
  }

  async setupVehicleCascade() {
    this.vMake = this.querySelector('[data-qv="make"]');
    if (!this.vMake) return;
    this.vModel = this.querySelector('[data-qv="model"]');
    this.vVariant = this.querySelector('[data-qv="variant"]');
    this.vModelWrap = this.querySelector('[data-qv-model-wrap]');
    this.vVariantWrap = this.querySelector('[data-qv-variant-wrap]');
    this.vOtherWrap = this.querySelector('[data-qv-other-wrap]');
    this.vOther = this.querySelector('[data-qv-other]');
    this.vValue = this.querySelector('[data-qv-value]');
    this.vSelects = this.querySelector('[data-qv-selects]');
    this.vNotListed = this.querySelector('[data-qv-notlisted]');

    try {
      this.vehicles = await fetch(this.dataset.vehiclesUrl).then((r) => r.json());
    } catch (e) {
      this.vehicles = [];
    }

    const makes = [...new Set(this.vehicles.map((v) => v.mk))].sort((a, b) => a.localeCompare(b));
    makes.forEach((m) => this.vMake.appendChild(this.optEl(m, m)));

    this.vMake.addEventListener('change', () => this.onVMake());
    this.vModel.addEventListener('change', () => this.onVModel());
    this.vVariant.addEventListener('change', () => this.updateVehicleValue());
    if (this.vOther) this.vOther.addEventListener('input', () => this.updateVehicleValue());
    if (this.vNotListed) this.vNotListed.addEventListener('change', () => this.toggleNotListed(true));
  }

  // "My vehicle isn't listed" -> free-text entry instead of the cascade.
  toggleNotListed(focus) {
    const manual = !!(this.vNotListed && this.vNotListed.checked);
    if (this.vSelects) this.vSelects.hidden = manual;
    this.vMake.required = !manual;
    if (manual) {
      this.vModel.required = false;
      this.vVariant.required = false;
    } else {
      this.vModel.required = !this.vModelWrap.hidden;
      this.vVariant.required = !this.vVariantWrap.hidden;
    }
    this.vOtherWrap.hidden = !manual;
    if (this.vOther) {
      this.vOther.required = manual;
      if (!manual) this.vOther.value = '';
      else if (focus) this.vOther.focus();
    }
    this.updateVehicleValue();
  }

  onVMake() {
    const mk = this.vMake.value;
    const models = [...new Set(this.vehicles.filter((v) => v.mk === mk).map((v) => v.md))].sort((a, b) =>
      a.localeCompare(b)
    );
    this.vModel.replaceChildren(this.optEl('', 'Select model'), ...models.map((m) => this.optEl(m, m)));
    this.vModelWrap.hidden = !mk;
    this.vModel.required = !!mk;
    this.vVariantWrap.hidden = true;
    this.vVariant.required = false;
    this._vVariants = [];
    this.updateVehicleValue();
  }

  onVModel() {
    const mk = this.vMake.value;
    const md = this.vModel.value;
    this._vVariants = this.vehicles.filter((v) => v.mk === mk && v.md === md);
    this.vVariant.replaceChildren(
      this.optEl('', 'Select variant'),
      ...this._vVariants.map((v, i) => this.optEl(String(i), v.tr))
    );
    const show = this._vVariants.length > 1 && !!md;
    this.vVariantWrap.hidden = !show;
    this.vVariant.required = show;
    this.updateVehicleValue();
  }

  updateVehicleValue() {
    if (!this.vValue) return;
    if (this.vNotListed && this.vNotListed.checked) {
      this.vValue.value = this.vOther ? this.vOther.value.trim() : '';
      return;
    }
    const mk = this.vMake.value;
    const md = this.vModel.value;
    if (!mk || !md) {
      this.vValue.value = '';
      return;
    }
    let val = `${mk} ${md}`;
    const vs = this._vVariants || [];
    let chosen = null;
    if (vs.length > 1) {
      const idx = parseInt(this.vVariant.value, 10);
      if (!isNaN(idx)) chosen = vs[idx];
    } else if (vs.length === 1) {
      chosen = vs[0];
    }
    if (chosen && chosen.tr && chosen.tr !== 'Standard') val += ` ${chosen.tr}`;
    this.vValue.value = val;
  }

  digitsOnly(value) {
    return (value || '').replace(/\D/g, '');
  }

  // Subscriber digits (after the area code), grouped for display.
  formatLocal(area, value) {
    const d = this.digitsOnly(value).slice(0, 8);
    if (area === '04') return [d.slice(0, 2), d.slice(2, 5), d.slice(5, 8)].filter(Boolean).join(' ');
    return [d.slice(0, 4), d.slice(4, 8)].filter(Boolean).join(' ');
  }

  // Full national number, formatted the Australian way.
  buildNational(area, value) {
    const d = this.digitsOnly(value).slice(0, 8);
    if (!d) return '';
    if (area === '04') {
      const full = ('04' + d).slice(0, 10);
      return full.length === 10 ? full.replace(/(\d{4})(\d{3})(\d{3})/, '$1 $2 $3') : full;
    }
    return d.length === 8 ? `(${area}) ${d.slice(0, 4)} ${d.slice(4)}` : `(${area}) ${d}`;
  }

  localDigits() {
    let d = this.digitsOnly(this.phoneInput.value);
    // If a full national number was pasted (e.g. 0412 345 678), drop the leading area code.
    if (d.length > 8 && d[0] === '0') d = d.slice(2);
    return d.slice(0, 8);
  }

  setupPhone() {
    if (!this.phoneInput || !this.areaSelect) return;
    const reformat = () => {
      const d = this.localDigits();
      this.phoneInput.value = this.formatLocal(this.areaSelect.value, d);
      if (this.phoneValue) this.phoneValue.value = this.buildNational(this.areaSelect.value, d);
    };
    this.phoneInput.addEventListener('input', reformat);
    this.areaSelect.addEventListener('change', reformat);
  }

  setupOther() {
    this.others = [];
    this.querySelectorAll('[data-quote-other-select]').forEach((select) => {
      const key = select.dataset.quoteOtherSelect;
      const wrap = this.querySelector(`[data-quote-other-for="${key}"]`);
      if (!wrap) return;
      const input = wrap.querySelector('[data-quote-other-input]');
      const otherOption = select.querySelector('option[value="__other"]');
      const entry = { select, wrap, input, otherOption };
      this.others.push(entry);

      const sync = (focus) => {
        const isOther = select.value === '__other';
        wrap.hidden = !isOther;
        if (!input) return;
        input.required = isOther;
        if (!isOther) input.value = '';
        else if (focus) input.focus();
      };
      select.addEventListener('change', () => sync(true));
      sync(false);
    });
  }

  // Combine the area code + phone, and push "Other" text into the select value,
  // right before the native form submission.
  prepareFields() {
    if (this.vValue) this.updateVehicleValue();
    if (this.areaSelect && this.phoneInput && this.phoneValue) {
      this.phoneValue.value = this.buildNational(this.areaSelect.value, this.localDigits());
    }
    (this.others || []).forEach((entry) => {
      if (entry.select.value === '__other' && entry.otherOption && entry.input) {
        entry.otherOption.value = entry.input.value.trim();
      }
    });
  }

  preselectChargerFromUrl() {
    const select = this.querySelector('#QuoteForm-charger');
    if (!select || select.tagName !== 'SELECT') return;

    const productId = new URLSearchParams(window.location.search).get('product_id');
    if (!productId) return;

    const option = select.querySelector(`option[data-product-id="${productId}"]`);
    if (!option) return;

    select.value = option.value;
    option.selected = true;
  }

  bindEvents() {
    if (this.fileInput) {
      this.fileInput.addEventListener('change', (event) => this.onFilesSelected(event));
    }

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', (event) => this.onSubmitClick(event));
    }

    if (this.form) {
      this.form.addEventListener('submit', (event) => this.onFormSubmit(event));
    }
  }

  setStatus(message, type = '') {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.classList.remove('quote-form__status--error', 'quote-form__status--success');
    if (type) this.statusEl.classList.add(`quote-form__status--${type}`);
  }

  onFilesSelected(event) {
    const selected = Array.from(event.target.files || []);
    const maxBytes = this.maxFileSizeMb * 1024 * 1024;

    for (const file of selected) {
      if (!file.type.startsWith('image/')) {
        this.setStatus(`${file.name} is not an image.`, 'error');
        continue;
      }

      if (file.size > maxBytes) {
        this.setStatus(`${file.name} exceeds ${this.maxFileSizeMb}MB.`, 'error');
        continue;
      }

      if (this.files.length >= this.maxFiles) {
        this.setStatus(`Maximum ${this.maxFiles} photos allowed.`, 'error');
        break;
      }

      this.files.push(file);
    }

    this.fileInput.value = '';
    this.renderFileList();
    this.setStatus('');
  }

  renderFileList() {
    if (!this.fileList) return;
    this.fileList.innerHTML = '';

    this.files.forEach((file, index) => {
      const item = document.createElement('li');
      item.className = 'quote-form__file-item';
      item.innerHTML = `
        <span>${file.name}</span>
        <button type="button" class="quote-form__file-remove" data-index="${index}">Remove</button>
      `;
      item.querySelector('button').addEventListener('click', () => {
        this.files.splice(index, 1);
        this.renderFileList();
      });
      this.fileList.appendChild(item);
    });
  }

  isSupabaseConfigured() {
    return Boolean(this.supabaseUrl && this.supabaseKey);
  }

  async uploadFiles() {
    if (!this.files.length) return [];

    if (!this.isSupabaseConfigured()) {
      throw new Error('Photo upload is not configured. Please contact us directly.');
    }

    if (!window.supabase?.createClient) {
      throw new Error('Upload library failed to load. Please refresh and try again.');
    }

    const client = window.supabase.createClient(this.supabaseUrl, this.supabaseKey);
    const urls = [];
    const folder = `quotes/${Date.now()}`;

    for (let i = 0; i < this.files.length; i++) {
      const file = this.files[i];
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `${folder}/${i + 1}-${safeName}`;

      this.setStatus(`Uploading photo ${i + 1} of ${this.files.length}…`);

      const { error } = await client.storage.from(this.bucket).upload(path, file, {
        cacheControl: '3600',
        upsert: false,
      });

      if (error) throw new Error(error.message);

      const { data } = client.storage.from(this.bucket).getPublicUrl(path);
      if (data?.publicUrl) urls.push(data.publicUrl);
    }

    return urls;
  }

  setLoading(loading) {
    if (this.submitBtn) {
      this.submitBtn.disabled = loading;
      this.submitBtn.setAttribute('aria-busy', loading ? 'true' : 'false');
    }
  }

  onFormSubmit(event) {
    event.preventDefault();
    event.stopPropagation();
    void this.submitQuote();
  }

  onSubmitClick(event) {
    event.preventDefault();
    void this.submitQuote();
  }

  async submitQuote() {
    if (this.isSubmitting || !this.form) return;

    if (!this.form.reportValidity()) return;

    this.prepareFields();

    this.isSubmitting = true;
    this.setLoading(true);

    try {
      if (this.files.length > 0) {
        const urls = await this.uploadFiles();
        if (this.photoUrlsInput) {
          this.photoUrlsInput.value = urls.map((u, i) => `Image ${i + 1}: ${u}`).join('\n\n');
        }
        this.setStatus('Photos uploaded. Sending your quote request…', 'success');
      }

      this.form.submit();
    } catch (error) {
      this.isSubmitting = false;
      this.setStatus(error.message || 'Upload failed. Please try again.', 'error');
      this.setLoading(false);
    }
  }
}

customElements.define('quote-form', QuoteForm);
